import { Link } from 'react-router-dom';
import { date, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, SearchInput, StatusBadge } from '../components/ui';

interface ProviderRow {
  id: string;
  name: string;
  isLive: boolean;
  teamSize: number;
  activeJobs: number;
  ratingAvg: string;
  ratingCount: number;
  policeVerification: string;
  createdAt: string;
  category: { id: string; name: string; sector: string };
  /** Their own name for the service, when the category is "Others". */
  customService: string | null;
  user: { id: string; mobile: string; kycStatus: string; isBlocked: boolean };
  _count: { locations: number; bookings: number };
}

export function ProvidersPage() {
  const { get, set } = useListParams();
  const search = get('search');

  const columns: Array<Column<ProviderRow>> = [
    {
      key: 'name',
      header: 'Provider',
      render: (r) => (
        <div>
          <div className="cell-strong">
            {r.name}
            {r.user.isBlocked && (
              <span style={{ marginLeft: 6 }}>
                <Badge tone="red">Blocked</Badge>
              </span>
            )}
          </div>
          <div className="cell-muted">
            {r.customService ? `${r.category.name} · ${r.customService}` : r.category.name}
          </div>
        </div>
      ),
    },
    {
      key: 'live',
      header: 'Availability',
      render: (r) => (
        <div className="row" style={{ gap: 5 }}>
          <Badge tone={r.isLive ? 'green' : 'neutral'} dot>
            {r.isLive ? 'Live' : 'Offline'}
          </Badge>
          {r.activeJobs >= r.teamSize && <Badge tone="amber">At capacity</Badge>}
        </div>
      ),
    },
    {
      key: 'capacity',
      header: 'Capacity',
      render: (r) => (
        <span>
          {r.activeJobs} / {r.teamSize}
        </span>
      ),
    },
    {
      key: 'rating',
      header: 'Rating',
      render: (r) =>
        r.ratingCount > 0 ? (
          <span>
            {Number(r.ratingAvg).toFixed(2)} <span className="cell-muted">({r.ratingCount})</span>
          </span>
        ) : (
          <span className="cell-muted">unrated</span>
        ),
    },
    { key: 'police', header: 'Police check', render: (r) => <StatusBadge value={r.policeVerification} /> },
    { key: 'kyc', header: 'KYC', render: (r) => <StatusBadge value={r.user.kycStatus} /> },
    { key: 'areas', header: 'Areas', numeric: true, render: (r) => num(r._count.locations) },
    { key: 'jobs', header: 'Bookings', numeric: true, render: (r) => num(r._count.bookings) },
    {
      key: 'media',
      header: 'Media',
      render: (r) => (
        <Link to={`/provider-media?providerId=${r.id}&review=ALL`} onClick={(e) => e.stopPropagation()}>
          Photos &amp; videos
        </Link>
      ),
    },
    {
      key: 'owner',
      header: 'Account',
      render: (r) => <Link to={`/users/${r.user.id}`}>{r.user.mobile}</Link>,
    },
    { key: 'joined', header: 'Joined', render: (r) => <span className="cell-muted">{date(r.createdAt)}</span> },
  ];

  return (
    <DataTable<ProviderRow>
      queryKey={['providers', search]}
      path="/admin/providers"
      params={{ search }}
      columns={columns}
      emptyIcon="⚒"
      emptyTitle="No providers match"
      toolbar={<SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Business name…" />}
    />
  );
}
